import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import cn from 'classnames';
import { compact } from 'lodash-es';
import { Box, StatusBadge, TableList, Typography, TableListActions, WithInfoIcon, Tooltip } from 'seagull';
import { Coins, EditSquare, Eye, InvoiceEdit } from 'seagull/icons';
import { fromRelationshipFundingSource, matchRelationshipPaymentMethodCell } from '~/business-logic/paymentMethodCellLogic';
import { useAgreementRowMenuItems } from '~/components/AgreementActions/AgreementRowMenuItems';
import { routes } from '~/constants';
import { useIoContext } from '~/contexts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { relationshipModel, relationshipSummaryModel, timeModel } from '~/models';
import { achOrCardLabel, verificationInitiatedText } from './AgreementsDashboard.table.utils';
import styles from './AgreementsDashboardRow.module.scss';
import { agreementStatusLabelsTextsPath, getAgreementStatusLabel } from './AgreementsDashboardRow.utils';
const AGREEMENT_STATUS_BADGE_SKIN = {
    [relationshipSummaryModel.AgreementStatus.Active]: 'success',
    [relationshipSummaryModel.AgreementStatus.Exhausted]: 'default',
    [relationshipSummaryModel.AgreementStatus.Terminated]: 'error'
};
export function AgreementsDashboardRow(props) {
    var _relationshipSummaryModel_agreementStatus, _AGREEMENT_STATUS_BADGE_SKIN_agreementStatus;
    const { isVendor, index, onVerifyPaymentMethod, onEditAgreement, onDownloadPDF, onDuplicateAgreement, onTerminateAgreement, onRenameAgreement, onChangeAssignee, highlighted, onHighlightEnd } = props;
    const { WIP_AMENDMENTS } = useFeatureFlags();
    const { t: dashboardTexts } = useLocaleKeys('agreement.dashboard');
    const { t: agreementStatusLabelsTexts } = useLocaleKeys(agreementStatusLabelsTextsPath);
    const { t: paymentMethodTexts } = useLocaleKeys('common.paymentMethod');
    const { t: timeTexts } = useLocaleKeys('common.time');
    const [agreement, setAgreement] = useState(props.agreement);
    useEffect(()=>setAgreement(props.agreement), [
        props.agreement
    ]);
    const { getNow } = useIoContext();
    const navigate = useNavigate();
    const displayedTitle = relationshipSummaryModel.displayedTitle(agreement, isVendor);
    const testId = agreement.isSample ? 'test-agreement' : "agreements-row-".concat(index);
    const heapId = agreement.isSample ? 'test-agreement' : 'agreement';
    const isTerminated = relationshipSummaryModel.isTerminated(agreement);
    const canCompleteVerification = relationshipSummaryModel.canCompleteVerification(agreement);
    const agreementStatus = (_relationshipSummaryModel_agreementStatus = relationshipSummaryModel.agreementStatus(agreement)) !== null && _relationshipSummaryModel_agreementStatus !== void 0 ? _relationshipSummaryModel_agreementStatus : '';
    const canRequestPM = !agreement.isSample && (!agreement.fundingSource || agreement.fundingSource.failedVerification()) && !relationshipSummaryModel.isTerminated(agreement) && isVendor;
    const lastModificationRecord = relationshipSummaryModel.lastModificationRecord(agreement);
    const agreementLink = isVendor || !relationshipModel.isPending(agreement) ? routes.agreementMinisiteRoutes(agreement.id).agreement : routes.clientOnboarding(agreement.id);
    const items = compact([
        {
            value: /*#__PURE__*/ _jsx(Box, {
                className: styles.title,
                children: /*#__PURE__*/ _jsxs(Typography, {
                    ellipsis: true,
                    hideTooltip: true,
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            ellipsis: true,
                            weight: "normal",
                            testId: "".concat(testId, "-company-name"),
                            tooltipTestId: "".concat(testId, "-company-name-tooltip"),
                            children: relationshipSummaryModel.opposingPartyName(agreement, isVendor)
                        }),
                        displayedTitle && /*#__PURE__*/ _jsx(Typography, {
                            ellipsis: true,
                            variant: "paragraph2",
                            skin: "secondary",
                            testId: "".concat(testId, "-title"),
                            tooltipTestId: "".concat(testId, "-title-tooltip"),
                            children: displayedTitle
                        })
                    ]
                })
            }),
            mobileValue: /*#__PURE__*/ _jsx(_Fragment, {
                children: /*#__PURE__*/ _jsx(Typography, {
                    as: "span",
                    ellipsis: true,
                    weight: "normal",
                    children: relationshipSummaryModel.opposingPartyName(agreement, isVendor)
                })
            }),
            mobileSecondaryValue: displayedTitle && /*#__PURE__*/ _jsx(Typography, {
                ellipsis: true,
                skin: "secondary",
                children: displayedTitle
            })
        },
        {
            value: /*#__PURE__*/ _jsxs(Box, {
                className: styles.status,
                children: [
                    agreementStatus && /*#__PURE__*/ _jsx(StatusBadge, {
                        skin: (_AGREEMENT_STATUS_BADGE_SKIN_agreementStatus = AGREEMENT_STATUS_BADGE_SKIN[agreementStatus]) !== null && _AGREEMENT_STATUS_BADGE_SKIN_agreementStatus !== void 0 ? _AGREEMENT_STATUS_BADGE_SKIN_agreementStatus : 'default',
                        info: agreementStatus === relationshipSummaryModel.AgreementStatus.Exhausted ? dashboardTexts.rowActions.allServicesCompleted() : undefined,
                        testId: "".concat(testId, "-status"),
                        children: getAgreementStatusLabel(agreementStatus, agreementStatusLabelsTexts)
                    }),
                    agreement.hasAmendments && /*#__PURE__*/ _jsx(Tooltip, {
                        placement: "top",
                        title: dashboardTexts.rowTooltips.awaitingApproval(),
                        children: /*#__PURE__*/ _jsx(Box, {
                            as: "span",
                            className: styles.amendmentIndicator,
                            testId: "".concat(testId, "-has-amendments"),
                            children: /*#__PURE__*/ _jsx(InvoiceEdit, {
                                size: 16
                            })
                        })
                    }),
                    WIP_AMENDMENTS && isVendor && agreement.hasAmendmentDraft && /*#__PURE__*/ _jsx(Tooltip, {
                        placement: "top",
                        title: dashboardTexts.rowTooltips.amendmentDraft(),
                        children: /*#__PURE__*/ _jsx(Box, {
                            as: "span",
                            className: styles.amendmentIndicator,
                            testId: "".concat(testId, "-has-amendment-draft"),
                            children: /*#__PURE__*/ _jsx(EditSquare, {
                                size: 16
                            })
                        })
                    })
                ]
            })
        },
        isVendor && {
            value: /*#__PURE__*/ _jsx(Typography, {
                ellipsis: true,
                skin: "secondary",
                testId: "".concat(testId, "-assignee"),
                children: relationshipSummaryModel.sellerFullName(agreement)
            })
        },
        WIP_AMENDMENTS && isVendor && {
            value: /*#__PURE__*/ _jsx(Typography, {
                ellipsis: true,
                hideTooltip: true,
                skin: "secondary",
                testId: "".concat(testId, "-last-modified-by"),
                children: lastModificationRecord && /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            ellipsis: true,
                            children: lastModificationRecord.savedByName
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            ellipsis: true,
                            variant: "paragraph2",
                            skin: "secondary",
                            children: timeModel.formatTimeAgo(new Date(lastModificationRecord.savedOn).getTime(), timeTexts, getNow().getTime())
                        })
                    ]
                })
            })
        },
        {
            value: /*#__PURE__*/ _jsx(Typography, {
                skin: "secondary",
                children: agreement.effectiveDate ? timeModel.formatDateLong(agreement.effectiveDate) : ''
            })
        },
        {
            // WHY: extracting this cell needs its own component and driver — out of scope for ANCR-5519
            value: matchRelationshipPaymentMethodCell(fromRelationshipFundingSource(agreement.fundingSource), {
                notSet: ()=>/*#__PURE__*/ _jsx(Typography, {
                        skin: "error",
                        children: dashboardTexts.notSet()
                    }),
                pendingDetails: ()=>/*#__PURE__*/ _jsx(WithInfoIcon, {
                        info: paymentMethodTexts.pendingDetailsTooltip(),
                        showInfo: true,
                        children: /*#__PURE__*/ _jsx(Typography, {
                            skin: "secondary",
                            children: paymentMethodTexts.pendingDetails()
                        })
                    }),
                set: (cellState)=>{
                    var _agreement_fundingSource, _agreement_fundingSource1;
                    return /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                skin: "secondary",
                                children: achOrCardLabel(cellState, paymentMethodTexts)
                            }),
                            ((_agreement_fundingSource = agreement.fundingSource) === null || _agreement_fundingSource === void 0 ? void 0 : _agreement_fundingSource.failedVerification()) && /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph2",
                                skin: "error",
                                children: dashboardTexts.verificationFailed()
                            }),
                            ((_agreement_fundingSource1 = agreement.fundingSource) === null || _agreement_fundingSource1 === void 0 ? void 0 : _agreement_fundingSource1.isPendingMicroDeposit()) && /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph2",
                                skin: "warning",
                                children: verificationInitiatedText(agreement.fundingSource, paymentMethodTexts)
                            })
                        ]
                    });
                }
            })
        },
        {
            value: agreement.lastBilledDate ? /*#__PURE__*/ _jsx(Typography, {
                skin: "secondary",
                testId: "".concat(testId, "-last-billed"),
                children: timeModel.formatDateLong(agreement.lastBilledDate)
            }) : null
        }
    ]);
    const getButtonText = ()=>{
        if (canCompleteVerification) return dashboardTexts.rowActions.completeVerification();
        return canRequestPM ? dashboardTexts.rowActions.addPaymentMethod() : undefined;
    };
    const onButtonClick = ()=>{
        if (canCompleteVerification) {
            onVerifyPaymentMethod(agreement);
        } else {
            navigate(routes.agreementMinisiteRoutes(agreement.id).paymentMethods);
        }
    };
    const menuRow = {
        id: agreement.id,
        title: agreement.title,
        draftId: agreement.draftId,
        isSample: agreement.isSample,
        customerName: relationshipSummaryModel.customerBusinessName(agreement),
        customerBusinessId: relationshipSummaryModel.customerBusinessId(agreement),
        status: agreement.status
    };
    const menuItems = useAgreementRowMenuItems({
        agreement: menuRow,
        isVendor,
        isTerminated,
        testId,
        heapId,
        onEditAgreement,
        onRenameAgreement,
        onRenameSuccess: (title)=>setAgreement(relationshipSummaryModel.setTitle(agreement, title)),
        onChangeAssignee,
        onDuplicateAgreement,
        onDownloadPDF,
        onTerminateAgreement
    });
    return /*#__PURE__*/ _jsx(TableList.Row, {
        onClick: ()=>navigate(agreementLink),
        items: items,
        rowId: agreement.id,
        testId: testId,
        className: cn({
            [styles.highlight]: highlighted
        }),
        onAnimationEnd: ()=>onHighlightEnd(agreement.id),
        "data-heapid": heapId,
        actionColumn: /*#__PURE__*/ _jsx(TableListActions, {
            bi: {
                button: {
                    name: 'agreements_primary_action'
                },
                secondaryButton: {
                    name: 'agreements_view'
                },
                menuTrigger: {
                    name: 'agreements_actions'
                }
            },
            testId: testId,
            menuHeapId: "".concat(heapId, "-menu-btn"),
            buttonText: getButtonText(),
            buttonIcon: /*#__PURE__*/ _jsx(Coins, {}),
            secondaryButtonText: dashboardTexts.rowActions.view(),
            secondaryBtnHeapId: "".concat(heapId, "-view-btn"),
            secondaryButtonIcon: /*#__PURE__*/ _jsx(Eye, {}),
            onClickButton: onButtonClick,
            onClickSecondaryButton: ()=>navigate(agreementLink),
            mobile: {
                title: relationshipSummaryModel.opposingPartyName(agreement, isVendor),
                subtitle: displayedTitle !== null && displayedTitle !== void 0 ? displayedTitle : undefined
            },
            children: menuItems.length > 0 && menuItems
        })
    });
}
